import React, { useEffect, useState } from "react";
import {
  searchMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";
import { FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MediaPickerModal = ({
  open,
  onClose,
  onSelect,
  defaultCategory = "pages",
}) => {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(defaultCategory || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  // Reset des filtres quand on ouvre
  useEffect(() => {
    if (!open) return;
    setQ("");
    setCategory(defaultCategory || "");
    setPage(1);
  }, [open, defaultCategory]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchMedia = async () => {
      try {
        setLoading(true);
        const res = await searchMedia({
          q: q.trim() || undefined,
          category: category.trim() || undefined,
          type: "image",
          page,
          limit,
          sort: "created_at",
          dir: "DESC",
        });
        if (cancelled) return;
        setItems(res.items || []);
        setTotalPages(res.totalPages || 1);
      } catch (e) {
        console.error("Erreur searchMedia (picker pages CMS):", e);
        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMedia();
    return () => {
      cancelled = true;
    };
  }, [open, q, category, page]);

  // Bloque le scroll de la page derrière
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSelect = (item) => {
    if (!item) return;
    onSelect?.(item);
  };

  const gotoPrev = () => setPage((p) => Math.max(p - 1, 1));
  const gotoNext = () => setPage((p) => Math.min(p + 1, totalPages));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-[1px]"
      onClick={handleBackdropClick}
    >
      <div className="relative z-10 w-[96vw] max-w-5xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h3 className="text-base font-semibold">Choisir une image (Page CMS)</h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 dark:hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Filtres */}
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 flex flex-col">
            <label className="text-xs font-semibold text-slate-600 mb-1">
              Rechercher dans la photothèque
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiSearch />
                </span>
                <input
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                  placeholder="Titre, tags, nom de fichier..."
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col min-w-[180px]">
            <label className="text-xs font-semibold text-slate-600 mb-1">
              Catégorie
            </label>
            <input
              value={category}
              onChange={(e) => {
                setPage(1);
                setCategory(e.target.value);
              }}
              placeholder="pages, bannières… (ou vide)"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Laisse vide pour toutes les catégories.
            </p>
          </div>
        </div>

        {/* Liste d'images */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-slate-500">Chargement des images…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">
              Aucune image trouvée. Vérifie tes filtres ou ajoute des médias
              dans la Photothèque.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {items.map((m) => {
                const url = toAbsolutePublicUrl(m.url);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => handleSelect(m)}
                    className="group flex flex-col rounded-lg border border-slate-200 overflow-hidden bg-slate-50 hover:border-sky-500 hover:shadow-md transition"
                  >
                    <div className="bg-slate-200 aspect-[4/3] overflow-hidden">
                      <img
                        src={url}
                        alt={m.alt_text || m.title || m.file_name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                      />
                    </div>
                    <div className="px-2 py-1.5 text-left">
                      <p className="text-[11px] font-semibold text-slate-800 line-clamp-2">
                        {m.title || "(Sans titre)"}
                      </p>
                      <p className="text-[10px] text-slate-500 line-clamp-1">
                        {m.category || "Sans catégorie"}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination & actions */}
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={gotoPrev}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40"
            >
              <FiChevronLeft />
              Précédent
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={gotoNext}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40"
            >
              Suivant
              <FiChevronRight />
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaPickerModal;
